/**
 * SoundExpertNode (Canvas V2)
 *
 * Redesigned audio generation node matching the borderless card pattern
 * used by VideoGeneratorNode and ImageGeneratorNode.
 *
 * Features:
 * - FloatingNodeLabel (editable name above card)
 * - Borderless card with selection ring via boxShadow
 * - Waveform display (40 bars, 100px height) with click-to-play
 * - Quick Info Bar: Config | Variants (2-column grid)
 * - UnifiedBottomPanel with Config + Variants tabs
 * - Config tab: top mode tabs, mode-specific controls, and compact generate tools
 * - Transparent handles (VideoGeneratorNode pattern)
 * - Toolbar: Generate, Download, Duplicate, Delete
 */

import { memo, useState, useCallback, useEffect, useMemo } from 'react'
import { NODE_CARD_RADIUS_CLASS } from '../shared/constants'
import { useDuplicateMenuItems } from '../shared/useDuplicateMenuItems'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow'
import { SlidersHorizontal, LayoutGrid, BookmarkPlus, Check, Loader2, Scissors, X } from 'lucide-react'
import { AddToChatIcon } from '../shared/AddToChatIcon'
import { createDebug, createNadDebug } from '../../../../utils/debugLog'
import { getAssetUrl } from '../../../../api/useAssetUrl'
import {
  applyVoiceCatalogueSelection,
  clearVoiceCatalogueSelection,
  executeNode,
  InsufficientSparksError,
  selectSoundExpertAudioVariant,
  trimSoundExpertAudio,
} from '../../../../api/client'
import { useCanvasOptional } from '../../CanvasContext'
import { useCanvasEdges } from '../../CanvasEdgesContext'
import { useHandlesVisibleForNode } from '../../HandleVisibilityContext'
import { useCanvasNodeChromeMode } from '../../CanvasNodeChromeModeContext'
import { dataStatus, dataStrOpt, readNodeDataFromYMapSafe, type ReactFlowCanvasEdgeData, ReactFlowCanvasNodeData } from '../../../../hooks/useYCanvas'
import {
  AgentNodeToolbar,
  FloatingNodeLabel,
  useNodeActions,
  useSaveToLibrary,
  useBottomPanelAutoOpen,
  useSelectSingleCanvasNode,
  UnifiedBottomPanel,
  FlatIdleNodeCard,
  getPickedIndex,
  soundAudioHistoryToJson,
  soundAudioPath,
  type HandleConfig,
  type ToolbarAction,
  type TabDefinition,
} from '../shared'
import { SoundExpertConfigTab, SOUND_ACCENT, type SoundMode } from '../shared/SoundExpertConfigTab'
import type { RichTextBoxState } from '../shared/RichTextBox'
import { AudioVariantsTabContent, type AudioVariant } from '../shared/AudioVariantsTabContent'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useActiveCanvasId } from '../../../../contexts/ActiveCanvasIdContext'
import { useShowInsufficientSparks } from '../../../../contexts/InsufficientSparksContext'
import { useClickLock } from '../../../../hooks/useClickLock'
import { type ReactFlowCanvasNodeDataByType, type SoundAudioHistoryEntryDict, type SoundAudioHistoryEntryDictYMap, type VoiceProfileDict } from '../../../../generated/nodeDataTypes'
import { buildAssetDownloadFilename } from '../../../../utils/buildDownloadFilename'
import { AudioWaveformVisualization } from '../shared/AudioWaveformVisualization'
import { MetaLoadingSurface, type MetaLoadingDescriptor } from '../shared/MetaLoadingSurface'
import { getWaveformPreviewAction, isDurationWithinSfxBound, resolveWaveformPreviewClickDecision } from './soundExpertUtils'
import {
  hasVoiceBriefForGeneratedSample,
  providerNeutralVoiceProfile,
} from './soundExpertVoiceProfile'
import { sanitizeNodeError } from '../../../../utils/sanitizeNodeError'
import { NodeStatusMessage } from '../shared/NodeStatusMessage'
import { SoundExpertTrimTab } from './SoundExpertTrimTab'

import { useCanvasNode } from '../../../../hooks/useCanvasNode'
import { useRegisterNodeContextAction } from '../../contextMenu/nodeContextActions'

// ─── Debug ─────────────────────────────────────────────────────────────────

const log = createDebug('dw:sound-expert:mode-switch')
const variantsPanelLog = createNadDebug('dw:variants-panel')
const voiceProfileLog = createNadDebug('dw:sound-expert-voice-profile')
const trimLog = createNadDebug('dw:sound-expert-audio-trim')
// Traces the capture-phase decision for waveform-preview clicks so a trace can
// show whether a click on the play control passed through to playback or was
// intercepted to open Config (Workplace post 1572398761184088).
const waveformClickLog = createNadDebug('dw:sound-expert:waveform-click')
const manualGenerateLog = createNadDebug('dw:sound-expert:manual-generate')

// ─── Constants ──────────────────────────────────────────────────────────────

const NODE_WIDTH = 315
const SOUND_FLAT_HANDLES: HandleConfig[] = [
  { type: 'target', position: Position.Top, id: 'in_upstream' },
  { type: 'target', position: Position.Left, id: 'in_voice_profile', style: { top: '30%' } },
  { type: 'target', position: Position.Left, id: 'in_text', style: { top: '70%' } },
  { type: 'source', position: Position.Bottom, id: 'out_audio' },
  { type: 'source', position: Position.Right, id: 'out_voice_profile', style: { top: '35%' } },
  { type: 'source', position: Position.Right, id: 'out_duration_seconds', style: { top: '65%' } },
]
type SoundExpertNodeData = ReactFlowCanvasNodeDataByType['soundExpert']
type SoundExpertConfigUpdateKey =
  | 'in_name'
  | 'in_mode'
  | 'in_prompt'
  | 'in_text'
  | 'in_duration'
  | 'in_loop'
  | 'in_lyrics_mode'
  | 'in_model'
type SoundExpertConfigUpdates = Partial<Pick<SoundExpertNodeData, SoundExpertConfigUpdateKey>>

function soundAudioHistoryEntries(value: unknown): unknown[] {
  if (!value || typeof value !== 'object') return []
  const history = (value as { history?: unknown }).history
  return Array.isArray(history) ? history : []
}

function soundAudioEntryPath(entry: unknown): string | undefined {
  if (!entry || typeof entry !== 'object') return undefined
  const result = (entry as { result?: unknown }).result
  if (!result || typeof result !== 'object') return undefined
  const path = (result as { local_path?: unknown }).local_path
  return typeof path === 'string' ? path : undefined
}

function isIdentityMatchingHistoryPrefix(current: unknown[], next: unknown[]): boolean {
  return (
    current.length < next.length &&
    current.every((entry, index) => {
      const currentPath = soundAudioEntryPath(entry)
      const nextPath = soundAudioEntryPath(next[index])
      return currentPath !== undefined && currentPath === nextPath
    })
  )
}

/** Waveform rendering constants */
const BAR_COUNT = 40
const WAVEFORM_HEIGHT = 100

const MODE_ICONS: Record<string, string> = {
  music: '\u{1F3B5}',
  speech: '\u{1F399}\uFE0F',
  sfx: '\u{1F4A5}',
}

/** Tabs for UnifiedBottomPanel */
const SOUND_EXPERT_TABS: TabDefinition[] = [
  { id: 'config', label: 'Config', icon: SlidersHorizontal },
  { id: 'variants', label: 'Variants', icon: LayoutGrid },
]

// ─── Audio History Entry ────────────────────────────────────────────────────

type AudioHistoryEntry = SoundAudioHistoryEntryDict
type AudioHistoryEntryYMap = SoundAudioHistoryEntryDictYMap

// ─── Speech Segment ─────────────────────────────────────────────────────────

interface SpeechSegment {
  character: string
  emotion: string
  timeRange: string
  audioUrl: string | null
  versions: number
  selectedVersion: number
}

// ─── Main Component ─────────────────────────────────────────────────────────

const SoundExpertNode = memo(function SoundExpertNode(props: NodeProps<SoundExpertNodeData>) {
  const nodePresentationWidth = useNodePresentationWidth()
  const { t } = useTranslation('canvas')
  const { id, data, selected, dragging } = props
  const status = dataStatus(data)
  const error = dataStrOpt(data, 'error')
  const cacheKey = dataStrOpt(data, 'cacheKey')
  const progressMessage = dataStrOpt(data, 'progressMessage')
  const projectId = useProjectId()
  const canvasId = useActiveCanvasId()

  const node = useCanvasNode(id, 'soundExpert', data)
  const out_audio = node?.get('out_audio')
  const in_name = node?.get('in_name') ?? ''
  const in_prompt = node?.get('in_prompt') ?? ''
  const in_mode = (node?.get('in_mode') ?? 'music') as SoundMode
  const in_duration = node?.get('in_duration') ?? '30s'
  const in_model = node?.get('in_model') ?? 'elevenlabs_v3'
  const in_loop = node?.get('in_loop') ?? 'off'
  const in_lyrics_mode = node?.get('in_lyrics_mode') ?? 'auto'
  const in_voice_profile = node?.get('in_voice_profile')
  const in_upstream = node?.get('in_upstream')
  const out_voice_profile = node?.get('out_voice_profile')
  const selected_voice_provider = node?.get('selected_voice_provider')
  const selected_voice_id = node?.get('selected_voice_id')
  const selected_voice_name = node?.get('selected_voice_name')
  const selected_voice_ref = node?.get('selected_voice_ref')
  const in_text = node?.get('in_text') ?? ''
  const showInsufficientSparks = useShowInsufficientSparks()
  // Per-node 1s cooldown so a fast double-tap on Generate can't fire
  // /execute-node twice.
  const { run: runGenerate } = useClickLock(1000)

  const { setNodes, getNodes } = useReactFlow<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData>()
  const edges = useCanvasEdges()
  const handlesVisible = useHandlesVisibleForNode(id)
  const canvas = useCanvasOptional()
  const { lightweightNodeChrome } = useCanvasNodeChromeMode()
  const [isHoveringLightweightNodeChrome, setIsHoveringLightweightNodeChrome] = useState(false)
  const {
    handleDelete,
    handleDuplicate,
    handleDownload,
    handleAddToChat,
  } = useNodeActions({ nodeId: id })
  const duplicateMenuItems = useDuplicateMenuItems(handleDuplicate, in_name || t('soundExpertNode.soundExpert'))
  const { saveState, handleSaveToLibrary, saveLabel } = useSaveToLibrary({ nodeId: id, selected })
  const selectSingleNode = useSelectSingleCanvasNode()

  // ── Per-mode prompt state (preserved across mode switches) ──────────────
  const [musicPrompt, setMusicPrompt] = useState(in_mode === 'music' ? in_prompt : '')
  const [sfxPrompt, setSfxPrompt] = useState(in_mode === 'sfx' ? in_prompt : '')
  const [speechContent, setSpeechContent] = useState(in_text)
  const targetSpeechVoiceBrief = useMemo(
    () => providerNeutralVoiceProfile(out_voice_profile ?? in_voice_profile),
    [out_voice_profile, in_voice_profile],
  )
  const selectedSpeechVoiceProfile = useMemo(() => {
    if (!selected_voice_provider && !selected_voice_id && !selected_voice_name && !selected_voice_ref) {
      return undefined
    }
    const profile: VoiceProfileDict = {}
    if (selected_voice_provider) profile.provider = selected_voice_provider
    if (selected_voice_id) profile.voice_id = selected_voice_id
    if (selected_voice_name) {
      profile.voice_name = selected_voice_name
      profile.name = selected_voice_name
    }
    if (selected_voice_ref) {
      profile.voice_ref = selected_voice_ref
      profile.voice_source = 'elevenlabs_voice_library'
    }
    return profile
  }, [
    selected_voice_provider,
    selected_voice_id,
    selected_voice_name,
    selected_voice_ref,
  ])
  const canGenerateBlankSpeechSample = useMemo(
    () => hasVoiceBriefForGeneratedSample(in_voice_profile ?? out_voice_profile),
    [in_voice_profile, out_voice_profile],
  )

  // ── Bottom panel state ──────────────────────────────────────────────────
  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('config')
  const [inlineTrimOpen, setInlineTrimOpen] = useState(false)
  // Variants now open as a dedicated, tab-less panel via the quick-info bar
  // (post 1533007285123236). Audio variants are a vertical waveform list (no
  // image grid), so the panel keeps its fixed size; only the tab bar is hidden.
  const variantsMode = activeTab === 'variants'
  const soundExpertTabs = useMemo(
    () => SOUND_EXPERT_TABS.filter((tab) => tab.id !== 'variants').map((tab) => ({
      ...tab,
      label: tab.id === 'config' ? t('soundExpertNode.config') : tab.label,
    })),
    [t],
  )

  // ── Audio URL resolution ──────────────────────────────────────────────────

  let audioPath: string | undefined
  const genAudioHistory = useMemo(
    (): AudioHistoryEntryYMap[] => {
      void cacheKey
      return out_audio?.history ?? []
    },
    [out_audio, cacheKey],
  )
  if (!audioPath && genAudioHistory.length) {
    const idx = getPickedIndex(out_audio)
    audioPath = soundAudioPath(genAudioHistory[idx])
  }
  const audioUrl =
    audioPath && projectId ? getAssetUrl(projectId, audioPath, 0, cacheKey) : null

  const isRunning = status === 'running'
  const isPendingExecution = status === 'pending_execution'
  const isPendingCostConfirmation = status === 'pending_cost_confirmation'
  const isBusy = isRunning || isPendingExecution || isPendingCostConfirmation
  const loadingMessage = progressMessage || (isPendingCostConfirmation
    ? t('baseNode.statusPendingApproval')
    : isPendingExecution
      ? t('baseNode.statusQueued')
      : t('soundExpertNode.generatingAudio'))
  const metaLoading = useMemo<MetaLoadingDescriptor | undefined>(() => {
    if (!isBusy) return undefined
    return {
      message: loadingMessage,
      nodeType: 'soundExpert',
      loadingState: isPendingCostConfirmation
        ? 'pending-cost'
        : isPendingExecution
          ? 'pending-execution'
          : 'running',
    }
  }, [isBusy, isPendingCostConfirmation, isPendingExecution, loadingMessage])
  const isError = status === 'error'
  const isVideoAudioExtractionNode =
    !!in_upstream &&
    typeof in_upstream === 'object' &&
    (in_upstream as { role?: unknown }).role === 'video_audio_extraction'
  const shouldRenderNodeChrome =
    !lightweightNodeChrome || !!selected || isHoveringLightweightNodeChrome
  const hasAudio = !!audioUrl
  const mode = in_mode

  // ── Stale-run detection ───────────────────────────────────────────────────

  useEffect(() => {
    if (status === 'running' && !isVideoAudioExtractionNode) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'idle', error: t('soundExpertNode.generationInterrupted') } }
            : n,
        ),
      )
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-open bottom panel ────────────────────────────────────────────────

  const batchPending = isBusy ? ((node?.get('batch_pending') as number | undefined) ?? 0) : 0

  useBottomPanelAutoOpen({
    selected,
    isRunning: isBusy,
    batchPending,
    setBottomPanelOpen,
    setActiveTab,
    defaultTab: 'config',
  })

  // ── History ───────────────────────────────────────────────────────────────

  const historyEntries = useMemo(
    (): AudioHistoryEntryYMap[] => genAudioHistory,
    [genAudioHistory],
  )
  const pickedIndex = getPickedIndex(out_audio)
  const selectedHistoryEntry = historyEntries[pickedIndex]
  const selectedDurationSeconds = selectedHistoryEntry?.metadata?.duration_seconds
  const historyEntryJson = useMemo(
    (): AudioHistoryEntry[] => soundAudioHistoryToJson(historyEntries),
    [historyEntries],
  )

  const handleHistorySelect = useCallback(
    async (index: number) => {
      const entry = historyEntries[index]
      const localPath = soundAudioPath(entry)
      if (!localPath || !projectId) return
      try {
        await selectSoundExpertAudioVariant(projectId, id, canvasId, index, localPath)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : String(error))
      }
    },
    [canvasId, id, historyEntries, projectId],
  )

  // ── Config updates ────────────────────────────────────────────────────────

  const updateConfig = useCallback(
    <K extends SoundExpertConfigUpdateKey,>(key: K, value: SoundExpertNodeData[K]) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [key]: value } } : n)),
      )
      canvas?.updateNodeData({ id, type: 'soundExpert' }, { [key]: value })
    },
    [id, setNodes, canvas],
  )

  // Batch multiple field updates into a single YDoc write to avoid
  // observer-echo race conditions (e.g. mode + prompt in one transaction).
  const updateConfigBatch = useCallback(
    (updates: SoundExpertConfigUpdates) => {
      log('updateConfigBatch: fields=%o', Object.keys(updates))
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...updates } } : n)),
      )
      canvas?.updateNodeData({ id, type: 'soundExpert' }, updates)
    },
    [id, setNodes, canvas],
  )

  const handleRename = useCallback(
    (newName: string) => {
      updateConfig('in_name', newName)
    },
    [updateConfig],
  )

  const handleModeChange = useCallback(
    (newMode: SoundMode) => {
      log('handleModeChange: %s -> %s', mode, newMode)
      // Save current prompt to per-mode state
      if (mode === 'music') setMusicPrompt(in_prompt)
      else if (mode === 'sfx') setSfxPrompt(in_prompt)

      // Restore the target mode's prompt
      const restoredPrompt =
        newMode === 'music' ? musicPrompt : newMode === 'sfx' ? sfxPrompt : ''

      // in_duration is ONE field shared by music and sfx, and the two modes
      // have different legal ranges (music offers 60s/120s; sfx is bounded at
      // SFX_DURATION_POLICY.maxSeconds). Carrying a music duration into sfx
      // used to be silently clamped backend-side; it is now refused, so a
      // stale 120s would turn Generate into a hard error with nothing in the
      // sfx picker looking wrong. Fall back to 'auto' — a real option the
      // picker renders as selected — and only when the value is actually
      // illegal, so in-range durations still carry over untouched.
      const carriedDurationIsIllegal =
        newMode === 'sfx' && !isDurationWithinSfxBound(in_duration)

      // Batch mode + prompt into a single YDoc write to prevent the observer
      // from echoing back stale field values between two separate writes.
      if (newMode !== 'speech') {
        log('handleModeChange: batching in_mode=%s + in_prompt (len=%d)', newMode, restoredPrompt.length)
        if (carriedDurationIsIllegal) {
          log('handleModeChange: resetting out-of-range in_duration=%s to auto', in_duration)
          updateConfigBatch({
            in_mode: newMode,
            in_prompt: restoredPrompt,
            in_duration: 'auto',
          })
        } else {
          updateConfigBatch({ in_mode: newMode, in_prompt: restoredPrompt })
        }
      } else {
        updateConfig('in_mode', newMode)
      }
    },
    [mode, in_prompt, in_duration, musicPrompt, sfxPrompt, updateConfig, updateConfigBatch],
  )

  const handleMusicPromptChange = useCallback(
    (value: string) => {
      setMusicPrompt(value)
      updateConfig('in_prompt', value)
    },
    [updateConfig],
  )

  const handleSfxPromptChange = useCallback(
    (value: string) => {
      setSfxPrompt(value)
      updateConfig('in_prompt', value)
    },
    [updateConfig],
  )

  const handleSpeechContentChange = useCallback(
    (state: RichTextBoxState) => {
      setSpeechContent(state.plainText)
      updateConfig('in_text', state.plainText)
    },
    [updateConfig],
  )

  const handleDurationChange = useCallback(
    (value: string) => updateConfig('in_duration', value),
    [updateConfig],
  )

  const handleLoopChange = useCallback(
    (value: string) => updateConfig('in_loop', value),
    [updateConfig],
  )

  const handleLyricsModeChange = useCallback(
    (value: string) => updateConfig('in_lyrics_mode', value),
    [updateConfig],
  )

  const handleModelChange = useCallback(
    (value: string) => updateConfig('in_model', value),
    [updateConfig],
  )

  const handleVoiceProfileChange = useCallback(
    async (profile: VoiceProfileDict | undefined) => {
      if (!projectId) throw new Error(t('soundExpertNode.noProjectSelected'))
      voiceProfileLog({
        event: 'voice-ownership:selection-start',
        node_id: id,
        voice_id: profile?.voice_id ?? '',
        voice_ref: profile?.voice_ref ?? '',
      })
      try {
        if (profile) {
          if (!profile.voice_ref) {
            throw new Error(t('soundConfigFlyover.voiceSelectionUnavailable', 'Voice selection is unavailable.'))
          }
          const result = await applyVoiceCatalogueSelection(
            projectId,
            canvasId,
            { type: 'sound_expert', node_id: id },
            profile.voice_ref,
          )
          result.warnings.filter(Boolean).forEach((warning) => toast.warning(warning))
          toast.success(result.message)
        } else {
          const result = await clearVoiceCatalogueSelection(projectId, canvasId, id)
          toast.success(result.message)
        }
        voiceProfileLog({ event: 'voice-ownership:selection-success', node_id: id })
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        voiceProfileLog({
          event: 'voice-ownership:selection-failure',
          node_id: id,
          error: message,
        })
        toast.error(message)
        throw error
      }
    },
    [canvasId, id, projectId, t],
  )

  // ── Validation ────────────────────────────────────────────────────────────

  const getValidationError = useCallback((): string | null => {
    if (mode === 'music' && !musicPrompt.trim())
      return t('soundExpertNode.promptRequiredMusic')
    if (mode === 'sfx' && !sfxPrompt.trim())
      return t('soundExpertNode.promptRequiredSfx')
    if (mode === 'speech' && !speechContent.trim() && !canGenerateBlankSpeechSample)
      return t('soundExpertNode.scriptTextRequired')
    if (!projectId) return t('soundExpertNode.noProjectSelected')
    return null
  }, [mode, musicPrompt, sfxPrompt, speechContent, canGenerateBlankSpeechSample, projectId, t])

  // ── Generate handler ──────────────────────────────────────────────────────

  const handleGenerate = useCallback(async () => {
    if (isBusy) return

    const validationError = getValidationError()
    if (validationError) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: validationError } }
            : n,
        ),
      )
      return
    }

    void runGenerate(id, async () => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'running', progress: 0, error: undefined } }
            : n,
        ),
      )

      try {
        const pid = projectId
        if (!pid) return

        manualGenerateLog({ event: 'manual-generate:dispatch', nodeId: id, outputCount: 1 })
        await executeNode(pid, id, { count: 1, canvasId })
      } catch (err) {
        if (err instanceof InsufficientSparksError) {
          showInsufficientSparks({
            balance: err.balance,
            required: err.required,
            nodeId: id,
            nodeType: 'sound_expert',
            projectId,
          })
          setNodes((nds) =>
            nds.map((n) =>
              n.id === id
                ? { ...n, data: { ...n.data, status: 'idle', error: undefined } }
                : n,
            ),
          )
          return
        }
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
              : n,
          ),
        )
      }
    })
  }, [
    projectId,
    canvasId,
    id,
    isBusy,
    setNodes,
    getValidationError,
    runGenerate,
    showInsufficientSparks,
  ])

  // ── Download handler ──────────────────────────────────────────────────────

  const onDownload = useCallback(() => {
    if (!audioPath) return
    const { filename } = buildAssetDownloadFilename({
      canvasNodes: getNodes(),
      canvasEdges: edges,
      nodeId: id,
      assetPath: audioPath,
      mediaType: 'audio',
      variantHistory: historyEntryJson,
      pickedIndex,
    })
    handleDownload(audioPath, filename)
  }, [audioPath, handleDownload, getNodes, edges, id, historyEntryJson, pickedIndex])
  useRegisterNodeContextAction(id, 'download', onDownload, Boolean(audioPath))

  // ── Trim handler ─────────────────────────────────────────────────────────

  const handleSaveTrim = useCallback(
    async ({ startS, endS }: { startS: number; endS: number }) => {
      if (!projectId || !audioPath) {
        throw new Error(t('soundExpertNode.trimNoAudio'))
      }
      trimLog({
        event: 'trim:save-start',
        node_id: id,
        source_path: audioPath,
        start_s: startS,
        end_s: endS,
        picked_index: pickedIndex,
      })
      const response = await trimSoundExpertAudio(projectId, id, canvasId, {
        historyIndex: pickedIndex,
        expectedSourcePath: audioPath,
        startS,
        endS,
      })
      const newCacheKey = String(Date.now())
      const currentYNode = canvas?.getYNode?.(id)
      const currentYData = currentYNode
        ? (readNodeDataFromYMapSafe(currentYNode, id) as Partial<SoundExpertNodeData>)
        : undefined
      const currentHistory = soundAudioHistoryEntries(currentYData?.out_audio)
      const responseHistory = soundAudioHistoryEntries(response.out_audio)
      const useResponseOutput =
        currentYData === undefined ||
        isIdentityMatchingHistoryPrefix(currentHistory, responseHistory)
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? {
                ...n,
                data: {
                  ...n.data,
                  out_audio: useResponseOutput ? response.out_audio : currentYData.out_audio,
                  out_duration_seconds:
                    useResponseOutput || typeof currentYData.out_duration_seconds !== 'number'
                      ? response.out_duration_seconds
                      : currentYData.out_duration_seconds,
                  cacheKey: newCacheKey,
                  error: undefined,
                },
              }
            : n,
        ),
      )
      canvas?.updateNodeData(
        { id, type: 'soundExpert' },
        {
          cacheKey: newCacheKey,
        },
      )
      trimLog({
        event: 'trim:saved',
        node_id: id,
        source_path: audioPath,
        trimmed_path: response.local_path,
        start_s: startS,
        end_s: endS,
        picked_index: response.picked_index,
      })
      setInlineTrimOpen(false)
      setActiveTab('variants')
      setBottomPanelOpen(true)
    },
    [audioPath, canvas, canvasId, id, pickedIndex, projectId, setNodes, t],
  )

  // ── Open panel to specific tab ───────────────────────────────────────────

  const openPanelToTab = useCallback(
    (tab: string) => {
      setInlineTrimOpen(false)
      setActiveTab(tab)
      setBottomPanelOpen(true)
      selectSingleNode(id)
      variantsPanelLog({
        event: 'bottom-panel:open',
        trace_id: `variants-panel:${id}`,
        node_id: id,
        node_type: 'soundExpert',
        mode: tab === 'variants' ? 'variants' : 'config',
        active_tab: tab,
      })
    },
    [id, setActiveTab, setBottomPanelOpen, selectSingleNode],
  )

  const openTrimInNode = useCallback(() => {
    setInlineTrimOpen(true)
    setBottomPanelOpen(false)
    selectSingleNode(id)
    trimLog({
      event: 'trim:inline-open',
      node_id: id,
      source_path: audioPath ?? '',
    })
  }, [audioPath, id, selectSingleNode])

  const handleWaveformPreviewClickCapture = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const action = getWaveformPreviewAction({ hasAudio, selected: !!selected })
      // A click on the play/pause control must reach the audio player's own
      // onClick (togglePlayback). `.closest()` resolves clicks that land on the
      // button's inner SVG icon or the loading spinner to the button, too.
      const targetIsPlayButton = !!(e.target as HTMLElement | null)?.closest(
        '[data-audio-control="true"], [data-testid="play-pause-btn"]',
      )
      const decision = resolveWaveformPreviewClickDecision({ action, targetIsPlayButton })
      // `swallowed` is the self-explanatory outcome: true means this click was
      // intercepted (stopPropagation) to open Config; false means it passed
      // through to the audio player. `previewAction` is what a non-button click
      // *would* do (from selection state) — not necessarily what happened.
      waveformClickLog({
        event: 'waveform-click-capture',
        node_id: id,
        hasAudio,
        selected: !!selected,
        previewAction: action,
        targetIsPlayButton,
        decision,
        swallowed: decision === 'open_config',
      })
      if (decision !== 'open_config') {
        // Pass the click through: the play button toggles playback, and a
        // waveform-bar click on a selected node seeks (handled inside
        // AudioWaveformVisualization).
        return
      }
      e.stopPropagation()
      openPanelToTab('config')
    },
    [id, hasAudio, selected, openPanelToTab],
  )

  // ── Toolbar actions ───────────────────────────────────────────────────────

  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'download',
        label: t('soundExpertNode.download'),
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
        ),
        onClick: onDownload,
        hidden: !hasAudio,
      },
      {
        key: 'addToChat',
        label: t('soundExpertNode.addToChat'),
        icon: <AddToChatIcon size={14} />,
        onClick: handleAddToChat,
      },
      {
        key: 'save-to-library',
        label: saveLabel,
        icon:
          saveState === 'loading' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : saveState === 'success' ? (
            <Check className="w-4 h-4 text-green-400" />
          ) : (
            <BookmarkPlus className="w-4 h-4" />
          ),
        onClick: handleSaveToLibrary,
        disabled: saveState !== 'idle',
        group: true,
      },
      {
        key: 'duplicate',
        label: t('soundExpertNode.duplicate'),
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        ),
        menuItems: duplicateMenuItems,
        group: true,
      },
      {
        key: 'delete',
        label: t('soundExpertNode.delete'),
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        ),
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [
      t,
      hasAudio,
      onDownload,
      duplicateMenuItems,
      handleDelete,
      handleAddToChat,
      handleSaveToLibrary,
      saveState,
      saveLabel,
    ],
  )

  // ── Variants data for bottom panel ────────────────────────────────────────

  const variants: AudioVariant[] = useMemo(
    () =>
      historyEntries.map((entry, i) => {
        const path = soundAudioPath(entry)
        return {
          index: i,
          audioUrl: path && projectId ? getAssetUrl(projectId, path, 0, cacheKey) : null,
          prompt: entry.user_prompt || '',
          mode: entry.metadata?.mode || 'music',
          durationSeconds: entry.metadata?.duration_seconds,
        }
      }),
    [historyEntries, projectId, cacheKey],
  )

  // ── Panel visibility ────────────────────────────────────────────────────

  const panelVisible =
    !!selected && !dragging && bottomPanelOpen 
  const showInlineTrimControl =
    hasAudio && shouldRenderNodeChrome && !!selected && !dragging && (panelVisible || inlineTrimOpen)

  // ── Render: Waveform area ─────────────────────────────────────────────────

  const renderWaveformArea = () => {
    if (metaLoading) {
      return (
        <div
          className="relative w-full"
          style={{ height: WAVEFORM_HEIGHT + 40 }}
        >
          <MetaLoadingSurface
            {...metaLoading}
            projectId={projectId}
            nodeId={id}
            presentation="full"
          />
        </div>
      )
    }

    if (hasAudio && audioUrl) {
      return (
        <div
          className="px-3 py-3"
          data-testid="waveform-area"
          onClickCapture={handleWaveformPreviewClickCapture}
        >
          <AudioWaveformVisualization
            url={audioUrl}
            height={WAVEFORM_HEIGHT}
            accentColor={SOUND_ACCENT.base}
          />
        </div>
      )
    }

    // Empty state — microphone icon + "No audio"
    return (
      <div
        className="w-full px-3 py-4 flex flex-col items-center justify-center"
        style={{ height: WAVEFORM_HEIGHT + 40 }}
        data-testid="empty-waveform"
        onClick={(e) => {
          e.stopPropagation()
          openPanelToTab('config')
        }}
      >
        <svg
          className="w-6 h-6 text-foreground/15 mt-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
          />
        </svg>
        <span className="text-xxs text-foreground/20 mt-1">{t('soundExpertNode.noAudio')}</span>
      </div>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (lightweightNodeChrome && !selected && !dragging && !isHoveringLightweightNodeChrome) {
    return (
      <div
        data-testid="sound-expert-lightweight-host"
        className="w-fit"
        onPointerEnter={() => setIsHoveringLightweightNodeChrome(true)}
        onPointerLeave={() => setIsHoveringLightweightNodeChrome(false)}
      >
        <FlatIdleNodeCard
          nodeId={id}
          testId="sound-expert-flat-node"
          label={in_name}
          fallbackLabel={t('soundExpertNode.soundExpert')}
          width={NODE_WIDTH}
          status={status}
          error={isError && error ? sanitizeNodeError(error, t, true) : undefined}
          progressMessage={loadingMessage}
          metaLoading={metaLoading}
          handles={SOUND_FLAT_HANDLES}
          mediaKind={hasAudio ? 'audio' : 'none'}
          mediaUrl={audioUrl}
          aspectRatio="16/9"
          placeholderLabel={hasAudio ? t('soundExpertNode.audio') : t('soundExpertNode.noAudio')}
        />
      </div>
    )
  }

  return (
    <div
      className="relative"
      data-testid="sound-expert-node"
      onPointerEnter={lightweightNodeChrome ? () => setIsHoveringLightweightNodeChrome(true) : undefined}
      onPointerLeave={lightweightNodeChrome ? () => setIsHoveringLightweightNodeChrome(false) : undefined}
    >
      <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

      {/* Floating label above the card */}
      <FloatingNodeLabel
        name={in_name}
        fallback={t('soundExpertNode.soundExpert')}
        onRename={handleRename}
      />

      <div
        className={`canvas-node-card-contained ${NODE_CARD_RADIUS_CLASS} overflow-hidden transition-all duration-150 ${
          isBusy ? 'border-2 animate-border-glow' : ''
        }`}
        style={{
          width: nodePresentationWidth(NODE_WIDTH),
          boxShadow: selected
            ? '0 0 0 2px rgba(255,255,255,0.7)'
            : isError
              ? '0 0 0 1px rgba(239,68,68,0.5)'
              : isBusy
                ? '0 0 0 1px rgba(6,182,212,0.5)'
                : '0 2px 8px rgba(0,0,0,0.3)',
        }}
      >
        {/* Connection handles (transparent, VideoGeneratorNode pattern) */}
        {handlesVisible && (
          <>
            <Handle
              type="target"
              position={Position.Top}
              id="in_upstream"
              className="!bg-transparent !border-0 !w-4 !h-1"
            />
            <Handle
              type="target"
              position={Position.Left}
              id="in_voice_profile"
              className="!bg-transparent !border-0 !w-1 !h-4"
              style={{ top: '30%' }}
            />
            <Handle
              type="target"
              position={Position.Left}
              id="in_text"
              className="!bg-transparent !border-0 !w-1 !h-4"
              style={{ top: '70%' }}
            />
            <Handle
              type="source"
              position={Position.Bottom}
              id="out_audio"
              className="!bg-transparent !border-0 !w-4 !h-1"
            />
            <Handle
              type="source"
              position={Position.Right}
              id="out_voice_profile"
              className="!bg-transparent !border-0 !w-1 !h-4"
              style={{ top: '35%' }}
            />
            <Handle
              type="source"
              position={Position.Right}
              id="out_duration_seconds"
              className="!bg-transparent !border-0 !w-1 !h-4"
              style={{ top: '65%' }}
            />
          </>
        )}

        {/* Waveform display area */}
        <div className="relative bg-canvas">
          {renderWaveformArea()}

          {/* Status overlay — floats over the waveform instead of adding a row,
              so a failure never changes the card's outline size (post
              1634117708345526). When earlier audio is kept, its play button and
              timer own the bottom edge, so the strip pins to the top instead. */}
          {isError && error && (
            <NodeStatusMessage
              message={sanitizeNodeError(error, t, true)}
              testId="sound-card-error"
              className={hasAudio && audioUrl ? 'top-0 bottom-auto border-t-0 border-b' : undefined}
            />
          )}
        </div>

        {/* Quick Info Bar — Config | Trim | Variants when the node is opened */}
        {!isBusy && shouldRenderNodeChrome && (
          <div
            data-testid="quick-info-bar"
            className={`${showInlineTrimControl ? 'grid-cols-3' : 'grid-cols-2'} grid border-t border-border-subtle bg-surface-0`}
          >
            {/* Config Column */}
            <button
              data-testid="quick-info-config"
              onClick={(e) => {
                e.stopPropagation()
                openPanelToTab('config')
              }}
              className="py-2.5 px-3 flex flex-col items-start gap-1 hover:bg-surface-1 transition-colors nodrag border-r border-border-subtle"
            >
              <span className="text-xxs text-muted-foreground leading-none">{t('soundExpertNode.config')}</span>
              <span className="text-xs font-medium text-muted-foreground truncate flex items-center gap-1">
                <span>{MODE_ICONS[mode] || ''}</span>
                {mode === 'music' ? t('soundExpertNode.modeMusic') : mode === 'speech' ? t('soundExpertNode.modeSpeech') : mode === 'sfx' ? t('soundExpertNode.modeSfx') : mode}
              </span>
            </button>

            {showInlineTrimControl && (
              <button
                data-testid="quick-info-trim"
                onClick={(e) => {
                  e.stopPropagation()
                  openTrimInNode()
                }}
                className={`py-2.5 px-3 flex flex-col items-start gap-1 transition-colors nodrag border-r border-border-subtle ${
                  inlineTrimOpen ? 'bg-surface-1 text-foreground' : 'hover:bg-surface-1'
                }`}
              >
                <span className="text-xxs text-muted-foreground leading-none">{t('soundExpertNode.trim')}</span>
                <span className="text-xs font-medium text-muted-foreground truncate flex items-center gap-1">
                  <Scissors className="h-3 w-3" />
                  {t('soundExpertNode.trimEdit')}
                </span>
              </button>
            )}

            {/* Variants Column */}
            <button
              data-testid="quick-info-variants"
              onClick={(e) => {
                e.stopPropagation()
                openPanelToTab('variants')
              }}
              className="py-2.5 px-3 flex flex-col items-start gap-1 hover:bg-surface-1 transition-colors nodrag"
            >
              <span className="text-xxs text-muted-foreground leading-none">{t('soundExpertNode.variants')}</span>
              <div className="flex items-center gap-1.5">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor:
                      historyEntries.length > 0 ? SOUND_ACCENT.base : 'var(--text-muted)',
                  }}
                />
                <span className="text-xs font-medium text-foreground">
                  {historyEntries.length}
                </span>
              </div>
            </button>
          </div>
        )}

        {inlineTrimOpen && hasAudio && (
          <div
            data-testid="sound-expert-inline-trim"
            className="border-t border-border-subtle bg-surface-0"
          >
            <div className="flex items-center justify-between border-b border-border-subtle px-3 py-2">
              <div className="flex min-w-0 items-center gap-2 text-xs font-medium text-foreground">
                <Scissors className="h-4 w-4 shrink-0 text-primary" />
                <span className="truncate">{t('soundExpertNode.trim')}</span>
              </div>
              <button
                type="button"
                aria-label={t('soundExpertNode.trimClose')}
                data-testid="sound-trim-close"
                onClick={(e) => {
                  e.stopPropagation()
                  setInlineTrimOpen(false)
                }}
                className="nodrag flex h-7 w-7 items-center justify-center rounded-lg border border-input-border bg-surface-1 text-muted-foreground transition-colors hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <SoundExpertTrimTab
              audioUrl={audioUrl}
              sourcePath={audioPath}
              durationSeconds={selectedDurationSeconds}
              disabled={isBusy}
              showWaveform={false}
              onSaveTrim={handleSaveTrim}
            />
          </div>
        )}
      </div>

      {/* UnifiedBottomPanel */}
      <UnifiedBottomPanel
        tabs={soundExpertTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isVisible={panelVisible}
        hideTabBar={variantsMode}
        minWidth={500}
        contentHeight={340}
      >
        {(tab) =>
          tab === 'config' ? (
            <SoundExpertConfigTab
              nodeId={id}
              mode={mode}
              musicPrompt={musicPrompt}
              sfxPrompt={sfxPrompt}
              speechContent={speechContent}
              duration={in_duration}
              loop={in_loop}
              lyricsMode={in_lyrics_mode}
              model={in_model}
              voiceProfile={selectedSpeechVoiceProfile ?? targetSpeechVoiceBrief}
              selectedVoiceProfile={selectedSpeechVoiceProfile}
              targetVoiceBrief={targetSpeechVoiceBrief}
              allowBlankSpeechWithVoiceProfile={canGenerateBlankSpeechSample}
              isRunning={isBusy}
              onModeChange={handleModeChange}
              onMusicPromptChange={handleMusicPromptChange}
              onSfxPromptChange={handleSfxPromptChange}
              onSpeechContentChange={handleSpeechContentChange}
              onDurationChange={handleDurationChange}
              onLoopChange={handleLoopChange}
              onLyricsModeChange={handleLyricsModeChange}
              onModelChange={handleModelChange}
              onVoiceProfileChange={handleVoiceProfileChange}
              onGenerate={handleGenerate}
            />
          ) : (
            <AudioVariantsTabContent
              variants={variants}
              selectedIndex={pickedIndex}
              batchPending={batchPending}
              onSelect={handleHistorySelect}
            />
          )
        }
      </UnifiedBottomPanel>
    </div>
  )
})

SoundExpertNode.displayName = 'SoundExpertNode'

export default SoundExpertNode
export { SoundExpertNode, NODE_WIDTH, BAR_COUNT, WAVEFORM_HEIGHT, SOUND_EXPERT_TABS }
export type { SpeechSegment }
import { useNodePresentationWidth } from '../../NodePresentationContext'
